<template>
  <!-- 首頁供應商輪播：清單接兩份複本循環捲動；複本不給輔助技術重複朗讀、不可 tab 聚焦 -->
  <div
    ref="rail"
    class="original-provider-list"
    :class="{ 'is-static': reducedMotion }"
    @pointerenter="onPointerEnter"
    @pointerleave="hovering = false"
    @pointerdown="onPointerDown"
    @touchstart.passive="touching = true"
    @scroll.passive="onScroll"
    @focusin="syncPlayState"
    @focusout="onFocusOut"
  >
    <a
      v-for="(provider, index) in loopProviders"
      :key="index"
      href="#"
      :class="{ 'original-provider-copy': index >= providers.length }"
      :aria-hidden="index >= providers.length ? 'true' : null"
      :tabindex="index >= providers.length ? -1 : null"
      @click.prevent="$emit('select', provider)"
    >
      <img :src="provider.sidebar_image_app" :alt="provider.name">
    </a>
  </div>
</template>

<script>
const LOOP_COPIES = 2
// 每毫秒捲動的 px（20 px/秒），全裝置統一、與切版一致（設計端定案）。
// 若再有「太慢」的回饋請先會設計端，不要直接調數值；手機「卡卡的」已改由合成器動畫處理，不是速度問題
const SCROLL_SPEED = 0.02
const RESUME_DELAY = 4000 // 使用者捲動（含 iOS 放開後的慣性滑動）停止後多久再自動捲動

const wrapOffset = (offset, cycle) => ((offset % cycle) + cycle) % cycle

export default {
  name: 'ProviderCarousel',
  props: {
    providers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      reducedMotion: false,
      hovering: false,
      touching: false
    }
  },
  computed: {
    loopProviders() {
      return Array.from({ length: LOOP_COPIES + 1 }).reduce(list => list.concat(this.providers), [])
    }
  },
  watch: {
    providers() {
      this.$nextTick(() => this.buildAnimation(0))
    },
    hovering() {
      this.syncPlayState()
    },
    touching() {
      this.syncPlayState()
    }
  },
  mounted() {
    this.visible = false
    this.resumeAt = 0
    this.cycle = 0
    this.animations = []
    this.appliedLeft = 0 // 程式最後一次設定後的 scrollLeft，用來分辨捲動是不是使用者造成的

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener('change', this.syncMotion)
    } else {
      this.motionQuery.addListener(this.syncMotion)
    }

    window.addEventListener('pointerup', this.release)
    window.addEventListener('pointercancel', this.release)
    window.addEventListener('touchend', this.release)
    window.addEventListener('touchcancel', this.release)
    window.addEventListener('resize', this.onResize)
    document.addEventListener('visibilitychange', this.syncPlayState)

    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting
      this.syncPlayState()
    })
    this.observer.observe(this.$refs.rail)

    this.syncMotion()
  },
  beforeDestroy() {
    this.cancelAnimation()
    clearTimeout(this.resumeTimer)
    cancelAnimationFrame(this.resizeFrame)
    if (this.motionQuery.removeEventListener) {
      this.motionQuery.removeEventListener('change', this.syncMotion)
    } else {
      this.motionQuery.removeListener(this.syncMotion)
    }
    window.removeEventListener('pointerup', this.release)
    window.removeEventListener('pointercancel', this.release)
    window.removeEventListener('touchend', this.release)
    window.removeEventListener('touchcancel', this.release)
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('visibilitychange', this.syncPlayState)
    this.observer.disconnect()
  },
  methods: {
    syncMotion() {
      this.reducedMotion = this.motionQuery.matches
      this.$nextTick(() => this.buildAnimation(0))
    },
    // 寬度變動時一輪的寬度跟著變，保留目前位置（換算成比例）重建動畫
    onResize() {
      cancelAnimationFrame(this.resizeFrame)
      this.resizeFrame = requestAnimationFrame(() => {
        const ratio = this.cycle ? this.getPosition() / this.cycle : 0
        this.buildAnimation(ratio)
      })
    },
    cancelAnimation() {
      this.animations.forEach(animation => animation.cancel())
      this.animations = []
    },
    // 自動捲動交給 Web Animations API：每個磚塊以 translate 位移，由合成器逐幀繪製，
    // 不受主執行緒忙碌掉幀影響，也不會因次像素位移重算抗鋸齒而顫動。
    // 用 translate 屬性而不是 transform，主題的 :active 按壓位移（transform）才能疊加上去
    // （不可用 @keyframes + CSS 變數：循環寬度是執行時量測值，參照變數會讓動畫掉回主執行緒）
    buildAnimation(ratio) {
      this.cancelAnimation()
      const rail = this.$refs.rail
      if (!rail) return
      rail.scrollLeft = 0
      this.appliedLeft = rail.scrollLeft
      this.cycle = 0

      const copyStart = rail.children[this.providers.length]
      if (this.reducedMotion || !copyStart || typeof rail.animate !== 'function') return

      // 一輪的寬度＝第一份複本第一項與原清單第一項的距離（含間距）
      const cycle = copyStart.offsetLeft - rail.children[0].offsetLeft
      if (cycle <= 0) return
      this.cycle = cycle

      const keyframes = [{ translate: '0 0' }, { translate: `${-cycle}px 0` }]
      const options = { duration: cycle / SCROLL_SPEED, iterations: Infinity }
      this.animations = Array.from(rail.children, item => item.animate(keyframes, options))
      this.setPosition(ratio * cycle)
      this.syncPlayState()
    },
    getPosition() {
      const animation = this.animations[0]
      return animation && this.cycle ? wrapOffset((animation.currentTime || 0) * SCROLL_SPEED, this.cycle) : 0
    },
    setPosition(position) {
      const time = position / SCROLL_SPEED
      this.animations.forEach(animation => {
        animation.pause()
        animation.currentTime = time
      })
    },
    isPlaying() {
      return this.animations.length > 0 && this.animations[0].playState === 'running'
    },
    // 滑鼠移入、觸控中、聚焦其中一項、畫面外、分頁隱藏或使用者剛捲動過時暫停，期間以使用者捲到的位置為準
    syncPlayState() {
      if (!this.animations.length) return
      const rail = this.$refs.rail
      const now = performance.now()
      const canMove = this.visible && !document.hidden && !this.hovering && !this.touching &&
        now >= this.resumeAt && !rail.contains(document.activeElement)

      if (canMove && !this.isPlaying()) {
        // 觸控、滾輪仍是原生捲動；恢復自動捲動時把使用者捲的距離併進位移，scrollLeft 歸零
        const position = wrapOffset(this.getPosition() + rail.scrollLeft, this.cycle)
        rail.scrollLeft = 0
        this.appliedLeft = rail.scrollLeft
        this.setPosition(position)
        // 所有磚塊設同一個 startTime，才會在同一幀起跑、保持對齊
        const startTime = document.timeline.currentTime - position / SCROLL_SPEED
        this.animations.forEach(animation => {
          animation.startTime = startTime
        })
      } else if (!canMove && this.isPlaying()) {
        this.setPosition(this.getPosition())
      }

      clearTimeout(this.resumeTimer)
      if (now < this.resumeAt) {
        this.resumeTimer = setTimeout(this.syncPlayState, this.resumeAt - now)
      }
    },
    onPointerEnter(event) {
      if (event.pointerType === 'mouse') this.hovering = true
    },
    // 觸控以 touch 事件判斷：iOS 開始滑動時就會送 pointercancel，手指其實還沒放開
    onPointerDown(event) {
      if (event.pointerType !== 'touch') this.touching = true
    },
    release(event) {
      if (event.pointerType === 'touch' || !this.touching) return
      this.touching = false
      this.pauseAutoScroll()
    },
    // focusout 時 activeElement 還沒換成下一個元素，等下一輪再判斷焦點是否已離開
    onFocusOut() {
      setTimeout(this.syncPlayState)
    },
    pauseAutoScroll() {
      this.resumeAt = performance.now() + RESUME_DELAY
      this.syncPlayState()
    },
    // 捲動位置跟程式設定的不同＝使用者在捲（iOS 放開手指後的慣性滑動也會一直觸發），
    // 暫停自動捲動直到停止後 RESUME_DELAY，避免動畫和原生捲動同時位移造成來回抖動
    onScroll() {
      if (Math.abs(this.$refs.rail.scrollLeft - this.appliedLeft) > 1) this.pauseAutoScroll()
    }
  }
}
</script>
